import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Instagram, Send, Globe, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  useEffect(() => {
    document.title = 'Contact Us | Multicolor International';
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.status === 'success') {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
        setTimeout(() => setStatus({ loading: false, success: false, error: null }), 5000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to send message. Please try again or email us directly.';
      setStatus({ loading: false, success: false, error: errorMessage });
      setTimeout(() => setStatus(s => ({ ...s, error: null })), 6000);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const offices = [
    {
      country: 'Pakistan',
      city: 'Lahore',
      flag: '🇵🇰',
      address: '129-C Rehmanpura, Lahore, Pakistan',
      phones: ['0321-4121313', '0337-4948221'],
      email: 'multicolor13@yahoo.com',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3401.842392!2d74.3067!3d31.5204!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDMxJzEzLjQiTiA3NMKwMTgnMjQuMSJF!5e0!3m2!1sen!2s!4v1234567890'
    },
    {
      country: 'United States',
      city: 'Carteret, New Jersey',
      flag: '🇺🇸',
      address: '11 Chrome Avenue, Carteret, New Jersey 07008, USA',
      phones: [],
      email: 'multicolor13@yahoo.com',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3029.1!2d-74.2267!3d40.5775!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDM0JzM5LjAiTiA3NMKwMTMnMzYuMSJX!5e0!3m2!1sen!2s!4v1234567890'
    },
    {
      country: 'Canada',
      city: 'Toronto',
      flag: '🇨🇦',
      address: 'Coming Soon - Toronto, Canada',
      phones: [],
      email: 'multicolor13@yahoo.com',
      mapUrl: null
    }
  ];

  const services = [
    'Screen/Offset Printing',
    'Labels & Stickers',
    'Packaging Solutions',
    'Industrial Labels',
    'Promotional Products',
    'Event Branding',
    'Corporate Gifts',
    'ID Cards & Badges',
    'Outdoor Signage',
    'Other'
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold mb-6" data-testid="contact-heading">
              Let's Create
              <br />
              <span className="gradient-text">Something Amazing</span>
            </h1>
            <p className="text-xl text-[#5B5B5B] max-w-3xl mx-auto">
              Get in touch with our team to discuss your printing, packaging, and branding needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="name">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6D] focus:border-transparent"
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="email">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6D] focus:border-transparent"
                      placeholder="john@company.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="phone">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6D] focus:border-transparent"
                      placeholder="+1 234 567 8900"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="company">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6D] focus:border-transparent"
                      placeholder="Your Company"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="service">
                    Service Required *
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6D] focus:border-transparent"
                    data-testid="contact-service-select"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="message">
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6D] focus:border-transparent resize-none"
                    placeholder="Tell us about your project..."
                    data-testid="contact-message-textarea"
                  />
                </div>

                <button
                  type="submit"
                  disabled={status.loading}
                  className="w-full btn-primary justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {status.loading ? (
                    <>
                      Sending... <Loader2 size={20} className="animate-spin" />
                    </>
                  ) : status.success ? (
                    <>
                      Message Sent! <Send size={20} />
                    </>
                  ) : (
                    <>
                      Send Message <Send size={20} />
                    </>
                  )}
                </button>

                {status.success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-center"
                    data-testid="contact-success-message"
                  >
                    Thank you! Your message has been sent to our team. We'll get back to you within 24 hours.
                  </motion.div>
                )}

                {status.error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-center"
                    data-testid="contact-error-message"
                  >
                    {status.error}
                  </motion.div>
                )}
              </form>
            </motion.div>

            {/* Quick Info */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold mb-6">Get in Touch</h2>
                <p className="text-lg text-[#5B5B5B] leading-relaxed">
                  Whether you need printing services, packaging solutions, or promotional products, our team is ready to help bring your vision to life.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#1B3A6D]/10 rounded-lg">
                    <Mail className="text-[#1B3A6D]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Email Us</h3>
                    <a
                      href="mailto:multicolor13@yahoo.com"
                      className="text-[#5B5B5B] hover:text-[#1B3A6D] transition"
                      data-testid="contact-email-link"
                    >
                      multicolor13@yahoo.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#1B3A6D]/10 rounded-lg">
                    <Phone className="text-[#1B3A6D]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Call Us</h3>
                    <div className="space-y-1">
                      <p className="text-[#5B5B5B]">Pakistan: 0321-4121313</p>
                      <p className="text-[#5B5B5B]">Pakistan: 0337-4948221</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#1B3A6D]/10 rounded-lg">
                    <Instagram className="text-[#1B3A6D]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Follow Us</h3>
                    <a
                      href="https://www.instagram.com/multicolorinternational"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#5B5B5B] hover:text-[#1B3A6D] transition"
                      data-testid="contact-instagram-link"
                    >
                      @multicolorinternational
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#1B3A6D]/10 rounded-lg">
                    <Clock className="text-[#1B3A6D]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Business Hours</h3>
                    <div className="text-[#5B5B5B] space-y-1">
                      <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p>Saturday: 9:00 AM - 2:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Our Offices</h2>
            <p className="text-lg text-[#5B5B5B]">
              Visit us at any of our international locations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {offices.map((office, index) => (
              <motion.div
                key={office.country}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg"
                data-testid={`office-${office.country.toLowerCase().replace(' ', '-')}`}
              >
                {office.mapUrl ? (
                  <div className="h-48 bg-gray-200 relative">
                    <iframe
                      src={office.mapUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen={false}
                      loading="lazy"
                      className="grayscale"
                      title={`${office.country} Office Map`}
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-[#1B3A6D] to-[#3FABF3] flex items-center justify-center">
                    <Globe size={64} className="text-white opacity-50" />
                  </div>
                )}

                <div className="p-6">
                  <div className="text-4xl mb-3">{office.flag}</div>
                  <h3 className="text-2xl font-bold mb-2">{office.country}</h3>
                  <p className="text-[#1B3A6D] font-semibold mb-4">{office.city}</p>

                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <MapPin size={18} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-[#5B5B5B]">{office.address}</p>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Phone size={18} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-[#5B5B5B]">
                        {office.phones.length > 0 ? (
                          office.phones.map((phone, i) => (
                            <p key={i}>{phone}</p>
                          ))
                        ) : (
                          <p className="italic text-gray-400">Email only</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Mail size={18} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <a
                        href={`mailto:${office.email}`}
                        className="text-sm text-[#5B5B5B] hover:text-[#1B3A6D] transition"
                      >
                        {office.email}
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;